import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NumberOrNull, StringOrNull } from '../entity/types';
import { RootState, TThunkApi } from 'store/store';
import * as manApi from 'api/ManApi';
import { LimitationType } from '../limitations/limitationsSlice';

export interface ISingleItemAccessToken {
  id?: number;
  itemId: NumberOrNull;
  permissionLevel: LimitationType;
  expirationTime: NumberOrNull;
  token: StringOrNull;
}

export interface ISingleItemAccessTokens {
  data: { [key: number]: ISingleItemAccessToken };
}

const initialState: ISingleItemAccessTokens = { data: {} };

function setSeveralSingleItemAccessTokenHandler(
  state: ISingleItemAccessTokens,
  action: PayloadAction<{ tokens: Array<Required<ISingleItemAccessToken>> }>
) {
  action.payload.tokens.forEach((token) => {
    state.data[token.id] = token;
  });
}

function setSingleItemAccessTokenHandler(
  state: ISingleItemAccessTokens,
  action: PayloadAction<{ token: ISingleItemAccessToken }>
) {
  state.data[action.payload.token.id!] = action.payload.token;
}

function deleteSingleItemAccessTokenHandler(
  state: ISingleItemAccessTokens,
  action: PayloadAction<{ token: ISingleItemAccessToken }>
) {
  delete state.data[action.payload.token.id!];
}

export const requireSingleItemAccessTokenFn = async (
  { itemId }: { itemId?: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .getSingleItemAccessTokens(store, itemId)
    .then(({ data }) => {
      ThunkApi.dispatch(setSeveralSingleItemAccessToken({ tokens: data }));
      return Promise.resolve({ tokens: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const requireSingleItemAccessToken = createAsyncThunk(
  'tokens/requireSingleItemAccessToken',
  requireSingleItemAccessTokenFn
);

export const saveSingleItemAccessTokenFn = async (
  {
    siat,
    isUpdate = false,
  }: { siat: ISingleItemAccessToken; isUpdate: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  if (isUpdate) {
    return manApi
      .updateSingleItemAccessToken(store, siat)
      .then(({ data }) => {
        ThunkApi.dispatch(setSingleItemAccessToken({ token: data }));
        return Promise.resolve({ token: data });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }

  return manApi
    .addSingleItemAccessToken(store, siat)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleItemAccessToken({ token: data }));
      return Promise.resolve({ token: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const saveSingleItemAccessToken = createAsyncThunk(
  'tokens/saveSingleItemAccessToken',
  saveSingleItemAccessTokenFn
);

export const deleteSingleItemAccessTokenFn = async (
  { siat }: { siat: ISingleItemAccessToken },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.deleteSingleItemAccessToken(store, siat.id!).then(
    () => {
      ThunkApi.dispatch(removeSingleItemAccessTokenAction({ token: siat }));
      return Promise.resolve({ token: siat });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const deleteSingleItemAccessToken = createAsyncThunk(
  'tokens/deleteSingleItemAccessToken',
  deleteSingleItemAccessTokenFn
);

const tokensSlice = createSlice({
  name: 'tokens',
  initialState: initialState,
  reducers: {
    setSeveralSingleItemAccessToken: setSeveralSingleItemAccessTokenHandler,
    setSingleItemAccessToken: setSingleItemAccessTokenHandler,
    removeSingleItemAccessTokenAction: deleteSingleItemAccessTokenHandler,
  },
});

export const {
  setSeveralSingleItemAccessToken,
  setSingleItemAccessToken,
  removeSingleItemAccessTokenAction,
} = tokensSlice.actions;

export {
  requireSingleItemAccessToken,
  saveSingleItemAccessToken,
  deleteSingleItemAccessToken,
};

export default tokensSlice.reducer;
